/**
 * ProductPackshotSection - Produkt-Kopfzeile + Bild-Auswahl-Grid
 *
 * Zeigt pro Produkt: Thumbnail, Name, SKU + alle analysierten Bilder
 * als klickbare PackshotImageCards.
 */

import { useState } from 'react'
import { Badge } from '@/components/v2/ui'
import { ImageIcon } from 'lucide-react'
import type { ProductPackshotStatus } from '@/lib/api/image-intelligence'
import { PackshotImageCard } from './PackshotImageCard'
import { PackshotPickerEmptyState } from './PackshotPickerEmptyState'

interface ProductPackshotSectionProps {
  productId: string
  name: string
  sku: string
  thumbnailUrl?: string
  status: ProductPackshotStatus | null
  selectedAssetId: string | null
  onSelect: (assetId: string) => void
  onStartAnalysis?: () => void
}

export function ProductPackshotSection({
  name, sku, thumbnailUrl, status, selectedAssetId, onSelect, onStartAnalysis,
}: ProductPackshotSectionProps) {
  const isAssigned = status?.has_packshot_assigned ?? false
  const rawCandidates = status?.all_analyses ?? (status?.best_candidate ? [status.best_candidate] : [])
  // Deduplizieren — Backend kann gleiche asset_id mehrfach liefern
  const candidates = rawCandidates.filter((c, i, arr) => arr.findIndex(x => x.asset_id === c.asset_id) === i)
  const currentAssetId = status?.best_candidate?.asset_id
  const hasAnalysis = candidates.length > 0

  return (
    <div className="packshot-product-section space-y-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
      <ProductSectionHeader
        name={name}
        sku={sku}
        thumbnailUrl={thumbnailUrl}
        isAssigned={isAssigned}
        candidateCount={candidates.length}
      />
      {hasAnalysis ? (
        <div className="flex flex-wrap gap-2">
          {candidates.map(c => (
            <PackshotImageCard
              key={c.asset_id}
              candidate={c}
              selected={selectedAssetId === c.asset_id}
              isCurrent={currentAssetId === c.asset_id && isAssigned}
              onClick={() => onSelect(c.asset_id)}
            />
          ))}
        </div>
      ) : (
        <PackshotPickerEmptyState onStartAnalysis={onStartAnalysis} />
      )}
    </div>
  )
}

function ProductSectionHeader({ name, sku, thumbnailUrl, isAssigned, candidateCount }: {
  name: string
  sku: string
  thumbnailUrl?: string
  isAssigned: boolean
  candidateCount: number
}) {
  const [imgError, setImgError] = useState(false)
  const showImg = !!thumbnailUrl && !imgError

  return (
    <div className="packshot-product-header flex items-center gap-3">
      {showImg ? (
        <img
          src={thumbnailUrl}
          alt={name}
          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
          <ImageIcon className="h-4 w-4 text-neutral-400" aria-hidden="true" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{name}</p>
        <p className="text-xs text-neutral-500">
          SKU: {sku}
          {candidateCount > 0 && (
            <span className="ml-2 text-neutral-400">· {candidateCount} Bild{candidateCount !== 1 ? 'er' : ''} analysiert</span>
          )}
        </p>
      </div>
      {isAssigned && (
        <Badge variant="success" className="flex-shrink-0 text-xs">✓ Gesetzt</Badge>
      )}
    </div>
  )
}
